import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  Typography,
  Chip
} from '@mui/material';

const EnhancedResultsTable = ({ results }) => {
  const [orderBy, setOrderBy] = useState('symbol');
  const [order, setOrder] = useState('asc');

  // Extract all available indicators from the first result, filtering out unwanted fields
  const availableIndicators = useMemo(() => {
    if (!results || !results.results || results.results.length === 0) return [];
    
    const firstResult = results.results[0];
    if (!firstResult.indicator_values) return [];
    
    // Filter out unwanted fields
    const unwantedFields = ['close', 'prev_close', 'volume', 'close_price', 'previous_close'];
    return Object.keys(firstResult.indicator_values).filter(key => 
      !unwantedFields.some(unwanted => key.toLowerCase().includes(unwanted.toLowerCase()))
    );
  }, [results]);

  // Process results to use backend-calculated price change
  const processedResults = useMemo(() => {
    if (!results || !results.results) return [];
    
    return results.results.map(stock => ({
      ...stock,
      changePercent: stock.price_change // Use backend-calculated price change
    }));
  }, [results]);

  // Define default columns (LTP and Change %)
  const defaultColumns = [
    { 
      key: 'symbol', 
      label: 'Symbol', 
      align: 'left',
      sortable: true,
      format: (value) => (
        <Typography 
          variant="body2" 
          sx={{ fontWeight: 600, color: 'primary.main' }}
        >
          {value}
        </Typography>
      )
    },
    { 
      key: 'last_price', 
      label: 'LTP', 
      align: 'right',
      sortable: true,
      format: (value) => (
        <Typography 
          variant="body2" 
          sx={{ fontWeight: 500 }}
        >
          {value ? `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
        </Typography>
      )
    },
    { 
      key: 'changePercent', 
      label: 'Change %', 
      align: 'right',
      sortable: true,
      format: (value, row) => {
        if (value === null || value === undefined) return (
          <Typography variant="body2" color="text.secondary">N/A</Typography>
        );
        const color = value >= 0 ? 'success.main' : 'error.main';
        const sign = value >= 0 ? '+' : '-';
        const bgColor = value >= 0 ? 'success.light' : 'error.light';
        return (
          <Box
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: `${bgColor}15`,
              display: 'inline-block',
              minWidth: 60,
              textAlign: 'center'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ color, fontWeight: 600, fontSize: '0.85rem' }}
            >
              {sign}{Math.abs(value).toFixed(2)}%
            </Typography>
          </Box>
        );
      }
    }
  ];

  // Dynamic indicator columns
  const indicatorColumns = availableIndicators.map(indicator => ({
    key: indicator,
    label: formatIndicatorLabel(indicator),
    align: 'right',
    sortable: true,
    format: (value) => {
      if (value === null || value === undefined) return (
        <Typography variant="body2" color="text.secondary">N/A</Typography>
      );
      
      // Format different indicator types
      const isPercentage = indicator.toLowerCase().includes('rsi') || 
                          indicator.toLowerCase().includes('percent') ||
                          indicator.toLowerCase().includes('%');
      
      const isPriceLevel = indicator.toLowerCase().includes('sma') || 
                          indicator.toLowerCase().includes('ema') ||
                          indicator.toLowerCase().includes('close') ||
                          indicator.toLowerCase().includes('price');
      
      const formattedValue = isPriceLevel 
        ? `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : isPercentage 
        ? `${value.toFixed(2)}%`
        : value.toFixed(2);
      
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            color: 'text.primary'
          }}
        >
          {formattedValue}
        </Typography>
      );
    }
  }));

  // Combine all columns
  const allColumns = [...defaultColumns, ...indicatorColumns];

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedResults = useMemo(() => {
    if (!processedResults) return [];
    
    return [...processedResults].sort((a, b) => {
      let aVal = getNestedValue(a, orderBy);
      let bVal = getNestedValue(b, orderBy);
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = -Infinity;
      if (bVal === null || bVal === undefined) bVal = -Infinity;
      
      // Convert to numbers if possible
      if (typeof aVal === 'string' && !isNaN(aVal)) aVal = parseFloat(aVal);
      if (typeof bVal === 'string' && !isNaN(bVal)) bVal = parseFloat(bVal);
      
      if (aVal < bVal) {
        return order === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [processedResults, orderBy, order]);

  if (!results || !results.results || results.results.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No results to display
      </Typography>
    );
  }

  return (
    <Box>
      {/* Column Info */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Showing:
        </Typography>
        <Chip label="LTP" size="small" variant="outlined" />
        <Chip label="Change %" size="small" variant="outlined" />
        {availableIndicators.map(indicator => (
          <Chip 
            key={indicator} 
            label={formatIndicatorLabel(indicator)} 
            size="small" 
            variant="outlined"
            color="primary"
          />
        ))}
      </Box>

      <TableContainer sx={{ maxHeight: 600, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {allColumns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{ 
                    fontWeight: 700,
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    whiteSpace: 'nowrap',
                    fontSize: '0.9rem',
                    py: 1.5,
                    '&:first-of-type': { pl: 2 },
                    '&:last-child': { pr: 2 }
                  }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.key}
                      direction={orderBy === column.key ? order : 'asc'}
                      onClick={() => handleRequestSort(column.key)}
                      sx={{
                        color: 'inherit !important',
                        '& .MuiTableSortLabel-icon': {
                          color: 'inherit !important'
                        }
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedResults.map((row, index) => (
              <TableRow 
                key={row.symbol} 
                hover
                sx={{ 
                  backgroundColor: 'background.paper',
                  '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                  '&:hover': { 
                    backgroundColor: 'action.selected',
                    '& .MuiTableCell-root': { 
                      color: 'text.primary'
                    }
                  },
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
              >
                {allColumns.map((column) => {
                  const value = getNestedValue(row, column.key);
                  const formattedValue = column.format ? column.format(value, row) : value;
                  
                  return (
                    <TableCell 
                      key={column.key} 
                      align={column.align}
                      sx={{ 
                        py: 1.5, 
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                        '&:first-of-type': { pl: 2 },
                        '&:last-child': { pr: 2 }
                      }}
                    >
                      {formattedValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {sortedResults.length} stocks found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sorted by: {formatIndicatorLabel(orderBy)} ({order === 'asc' ? 'Ascending' : 'Descending'})
        </Typography>
      </Box>
    </Box>
  );
};

// Helper function to get nested values (e.g., indicator_values.RSI_14)
const getNestedValue = (obj, path) => {
  if (path === 'changePercent') return obj.changePercent;
  if (path in obj) return obj[path];
  if (obj.indicator_values && path in obj.indicator_values) {
    return obj.indicator_values[path];
  }
  return null;
};

// Helper function to format indicator labels
const formatIndicatorLabel = (key) => {
  if (key === 'symbol') return 'Symbol';
  if (key === 'last_price') return 'LTP';
  if (key === 'changePercent') return 'Change %';
  
  // Format indicator names
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
    .replace(/(\d+)/g, '($1)'); // Wrap numbers in parentheses
};

export default EnhancedResultsTable;